import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import Button from "@mui/material/Button";
import { fnGetDataSet } from "@shared/api/restApi";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import SnSearchBtn from "@shared/components/SnSearchBtn";
import SnGrid from "@shared/components/GridV2/SnGrid";
import {
  extractAddInfoFlags,
  hydrateRowsWithAddInfo,
} from "@shared/utils/AddInfoUtils";
interface popupProps {
  selectedData?: any;
  modalRef?: any;
}

const SN_PRD_WMO_REG_003_POP_01 = forwardRef(
  ({ selectedData, modalRef }: popupProps, ref: React.Ref<any>) => {
    //#region variable
    const { userInfo } = global();
    const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
      grid1: React.createRef(),
      grid2: React.createRef(),
      grid3: React.createRef(),
    });
    const searchRef = useRef<any>(null);
    //#endregion

    //#region Components useState
    const [formData, setFormData] = useState<any>();
    const [formControlData, setFormControlData] = useState<any[]>([]);
    const [columns, setColums] = useState<any>({
      grid1: [],
      grid2: [],
      grid3: [],
    });
    const [dataSources, setDataSources] = useState<any>({
      grid1: [],
      grid2: [],
      grid3: [],
    });
    const [addInfoFlags, setAddInfoFlags] = useState<string[]>([]);
    //#endregion
    // useImperativeHandle(ref, () => ({
    //   getSelectedRow: () => [gridApiRef1.current?.getSelectedRows() || []],
    // }));

    useImperativeHandle(ref, () => ({
      getOkayData: () => {
        return {
          rtnData: [gridRefs.current["grid1"].current?.getCheckedRows() || []],
        };
      },
    }));

    const loadAddInfoFlags = async (idTarget = "grid2") => {
      try {
        const data: any = await fnGetDataSet({
          id: "sys_addinfo_pop_01_01",
          params: {
            cd_firm: userInfo?.cd_firm ?? "",
            cd_menu: "SN_SAL_SAO_REG_001",
            id_target: idTarget,
          },
        });

        const metaRows = data?.[0] || [];
        const orderedMetaRows = [...metaRows].sort(
          (a: any, b: any) => Number(a?.no_line ?? 0) - Number(b?.no_line ?? 0)
        );
        const flags = extractAddInfoFlags(orderedMetaRows);
        const flagNames = (orderedMetaRows || []).reduce(
          (acc: Record<string, string>, row: any) => {
            const cdFlag = String(row?.cd_flag ?? "").trim();
            const nmFlag = String(row?.nm_flag ?? "").trim();
            if (cdFlag) {
              acc[cdFlag] = nmFlag || cdFlag;
            }
            return acc;
          },
          {}
        );
        const flagOrders = (orderedMetaRows || []).reduce(
          (acc: Record<string, number>, row: any) => {
            const cdFlag = String(row?.cd_flag ?? "").trim();
            const noLine = Number(row?.no_line ?? 0);
            if (cdFlag) {
              acc[cdFlag] = Number.isFinite(noLine) ? noLine : 0;
            }
            return acc;
          },
          {}
        );

        setAddInfoFlags(flags);
        return { flags, flagNames, flagOrders };
      } catch (error: any) {
        setAddInfoFlags([]);
        return {
          flags: [],
          flagNames: {} as Record<string, string>,
          flagOrders: {} as Record<string, number>,
        };
      }
    };

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitPopUpData(
            "SN_PRD_WMO_REG_003_POP_01",
            {}
          );
          const mergedData: any = { ...rtn.formData, ...selectedData };
          const { flags, flagNames, flagOrders } =
            await loadAddInfoFlags("grid2");

          const baseGrid1Col = rtn?.grid?.grid1?.[0] || {};
          const nextSeqBase = Array.isArray(rtn?.grid?.grid1)
            ? rtn.grid.grid1.length
            : 0;
          const addInfoColumns = flags.map((flag: string, index: number) => ({
            ...baseGrid1Col,
            cd_column: flag,
            nm_column: flagNames[flag] || flag,
            tp_column: "SnTextBox",
            fg_visible: "Y",
            yn_required: "N",
            yn_sum: "N",
            yn_allowsorting: "Y",
            yn_fixed: "N",
            qt_width: 120,
            no_seq: nextSeqBase + (flagOrders[flag] || index + 1),
            dc_editcon: "",
            cd_popup: "",
            tp_format: "",
            nm_group: "",
          }));
          setFormControlData(rtn.formControlData); // 조회조건 항목 설정
          setFormData(mergedData); // 조회조건 초기값
          setColums({
            //grid1: rtn.grid.grid1,
            grid1: [...rtn.grid.grid1, ...addInfoColumns],
            grid2: rtn.grid.grid2,
            grid3: rtn.grid.grid3,
          });
        } catch (error) {}
      };
      fetchform();
    }, [userInfo]);

    //#region Button Event
    const onSearch = () => {
      const fetchdatasource = async () => {
        try {
          const params = {
            id: "sn_prd_wmo_reg_003_pop-02",
            params: searchRef.current.getFormdata(),
          };

          gridRefs.current["grid1"].current.resetUpdate();
          gridRefs.current["grid2"].current.resetUpdate();
          gridRefs.current["grid3"].current.resetUpdate();
          const data: any = await fnGetDataSet(params);
          const flags =
            addInfoFlags.length > 0
              ? addInfoFlags
              : (await loadAddInfoFlags("grid2")).flags;
          const sourceRows = (data[0] || []).map((row: any) => ({
            ...row,
            js_addinfo: row?.js_addinfo ?? row?.js_addinfo_h,
          }));
          setDataSources({
            grid1: hydrateRowsWithAddInfo(sourceRows, flags),
          });
        } catch (error) {
          console.log(error);
        }
      };
      fetchdatasource();
    };

    //#endregion

    //#region Event Method
    const onFocusedRowChanged = async (event: any) => {
      const selectedRow = event; // 클릭한 행 데이터
      if (selectedRow) {
        const Params = {
          cd_firm: userInfo.cd_firm,
          cd_item: selectedRow.cd_item,
          no_so: selectedRow.no_so,
        };
        // 배열로 만들어서 필터링 함수 호출
        const params = {
          id: "sn_prd_wmo_reg_003_03",
          params: Params,
        };
        gridRefs.current["grid1"].current.resetUpdate();
        gridRefs.current["grid2"].current.resetUpdate();
        gridRefs.current["grid3"].current.resetUpdate();
        const data: any = await fnGetDataSet(params);
        setDataSources((prev: any) => ({
          ...prev,
          grid2: data[0] || [],
          grid3: data[1] || [],
        }));
      } else {
        return;
      }
    };

    //#endregion

    const grids = useMemo(
      () => ({
        grid1: {
          columns: columns.grid1,
          dataSource: dataSources.grid1,
          events: {
            onFocusedRowChanged: onFocusedRowChanged,
          },
        },
        grid2: {
          columns: columns.grid2,
          dataSource: dataSources.grid2,
          events: {},
        },
        grid3: {
          columns: columns.grid3,
          dataSource: dataSources.grid3,
          events: {},
        },
      }),
      [columns, dataSources]
    );

    return (
      <div>
        <div>
          <SearchPanel
            ref={searchRef}
            data={formData}
            controls={formControlData}
            popupRef={ref}
          ></SearchPanel>
        </div>
        <div style={{ textAlign: "center" }}>
          <SnSearchBtn
            text={"조회"}
            onClick={onSearch}
            sx={{ width: 80, marginTop: 1.5 }}
            spinner={true}
          />
        </div>
        <div className="w-full h-full">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "multiple", ischeckboxSelection: true }}
            events={grids.grid1.events}
          />
        </div>
        <div className="flex flex-row h-full w-ful">
          <div className="w-1/2 h-full">
            <SnGrid
              ref={gridRefs.current["grid2"]}
              columnConfig={grids.grid2.columns}
              rowData={grids.grid2.dataSource}
              etcProps={{ rowSelection: "single", ischeckboxSelection: false }}
            />
          </div>
          <div className="w-1/2 h-full">
            <SnGrid
              ref={gridRefs.current["grid3"]}
              columnConfig={grids.grid3.columns}
              rowData={grids.grid3.dataSource}
              etcProps={{ rowSelection: "single", ischeckboxSelection: false }}
            />
          </div>
        </div>
      </div>
    );
  }
);

export default SN_PRD_WMO_REG_003_POP_01;
